// TikTok pixel events — every call is gated on advertising consent (lib/consent.js); without it nothing is queued or sent.
import { hasAdConsent } from "@/lib/consent";

const CURRENCY = "AUD";
const BRAND = "The Happy Bulldog Co.";

export const track = (event, data = {}) => {
  if (!hasAdConsent()) return;
  try { window.ttq?.track?.(event, { currency: CURRENCY, ...data }); } catch { /* never break the shop over analytics */ }
};

let lastPageKey = "";
export const pixelPage = () => {
  if (!hasAdConsent()) return;
  const key = window.location.pathname + window.location.search;
  if (key === lastPageKey) return; // StrictMode / re-render guard
  lastPageKey = key;
  try { window.ttq?.page?.(); } catch { /* analytics only */ }
};

// PII must be SHA-256 hashed client-side before TikTok sees it
const sha256 = async (value) => {
  const bytes = new TextEncoder().encode(value);
  const digest = await window.crypto.subtle.digest("SHA-256", bytes);
  return Array.from(new Uint8Array(digest)).map((b) => b.toString(16).padStart(2, "0")).join("");
};

const normalisePhone = (phone) => {
  const digits = String(phone || "").replace(/[^\d+]/g, "");
  if (!digits) return "";
  if (digits.startsWith("+")) return digits;
  if (digits.startsWith("0")) return `+61${digits.slice(1)}`; // AU local → E.164
  return `+${digits}`;
};

export const identify = async ({ email, phone, id } = {}) => {
  if (!hasAdConsent()) return;
  try {
    if (!window.ttq?.identify || !window.crypto?.subtle) return;
    const payload = {};
    const mail = String(email || "").trim().toLowerCase();
    const tel = normalisePhone(phone);
    if (mail) payload.email = await sha256(mail);
    if (tel) payload.phone_number = await sha256(tel);
    if (id) payload.external_id = await sha256(String(id));
    if (Object.keys(payload).length) window.ttq.identify(payload);
  } catch { /* analytics only */ }
};

const productContent = (p) => ({
  content_id: String(p.id),
  content_type: "product",
  content_name: p.name,
  content_category: p.category || p.collection || undefined,
  price: Number(p.sale_price || p.price) || 0,
  brand: BRAND,
});

export const lineContents = (lines) => lines.map((l) => ({
  content_id: l.type === "bundle" ? l.kind || "bundle" : String(l.id),
  content_type: l.type === "bundle" ? "product_group" : "product",
  content_name: l.name,
  content_category: l.type === "bundle" ? "bundle" : l.category || undefined,
  quantity: l.quantity || 1,
  price: Number(l.price) || 0,
  brand: BRAND,
}));

const linesValue = (lines) => lines.reduce((s, l) => s + (Number(l.price) || 0) * (l.quantity || 1), 0);

export const trackViewContent = (product) => track("ViewContent", {
  contents: [productContent(product)],
  value: Number(product.sale_price || product.price) || 0,
  description: product.short_description || product.description?.slice(0, 200) || undefined,
});

export const trackAddToWishlist = (product) => track("AddToWishlist", {
  contents: [productContent(product)],
  value: Number(product.sale_price || product.price) || 0,
});

let lastSearch = "";
export const trackSearch = (term, products = []) => {
  if (term === lastSearch) return; // the shop refetches a few times per filter change
  lastSearch = term;
  track("Search", { search_string: term, contents: products.slice(0, 10).map(productContent), value: 0 });
};

export const trackAddToCart = (line) => track("AddToCart", { contents: lineContents([line]), value: linesValue([line]) });

export const trackInitiateCheckout = (cart, value) => track("InitiateCheckout", { contents: lineContents(cart), value: Number(value) || 0 });

// Fired together when the shopper is handed to Stripe: they've committed to the order and are about to enter card details
export const trackPlaceAnOrder = (cart, value) => {
  const data = { contents: lineContents(cart), value: Number(value) || 0 };
  track("AddPaymentInfo", data);
  track("PlaceAnOrder", data);
};

export const trackCompleteRegistration = (user) => track("CompleteRegistration", {
  contents: [{ content_id: "account", content_type: "product", content_name: "Pack account" }],
  value: 0,
  ...(user?.method ? { description: user.method } : {}),
});

export const trackCompletePayment = (status) => {
  // fire once per Stripe session even if the success page is refreshed
  const key = `ttq_paid_${status.session_id}`;
  if (!status?.session_id || sessionStorage.getItem(key)) return;
  sessionStorage.setItem(key, "1");
  track("CompletePayment", { contents: lineContents(status.items || []), value: Number(status.total) || 0, order_id: status.order_number });
};
